// Screen navigation, inputs, mid-quiz UI, loading — depends on funnel-data.js + funnel-analysis.js
import {
  state,
  FLOW,
  PHASES,
  Q_IDS,
  TOASTS,
  phaseNodeFor,
  stripLeadingLoopProfileTitle,
  BALANCED_MAIN_PATTERN,
  BALANCED_MQI_A_BODY,
  BALANCED_MQI_A_FACT,
  BALANCED_MS1,
  BALANCED_MQI_B_TRAINING,
  MQI_A_FACT,
  MQI_A,
  MQI_B,
  ECHOES,
  ECHO_SOURCE,
  REACT_READY,
  LOOP_SHARE,
  LOOP_METHOD,
  LOOP_TESTIMONIALS,
  LOOP_TINT,
  TRIED_REACTIONS
} from './funnel-data.js';
import { buildAnalysis, isBalancedMindProfile, patternSentence, triedHeadline } from './funnel-analysis.js';
import { shareResult, buildShareUrl, flashCopied, renderCard } from './share.js';
import { SOCIAL_LOOP_SVG } from './share-templates.js';

var _navBusy = false;

/** Clears pending auto-advance after single-select taps (prevents double `next()` if user switches option quickly). */
var _autoAdvanceTimer = null;
function clearAutoAdvance() {
  if (_autoAdvanceTimer) {
    clearTimeout(_autoAdvanceTimer);
    _autoAdvanceTimer = null;
  }
}

function next() {
  if (_navBusy) return;
  clearAutoAdvance();
  state.cur++;
  let nid = FLOW[state.cur];
  if (nid === 'react-tried' && !(Array.isArray(state.D.c8) && state.D.c8.length)) {
    state.cur++;
    nid = FLOW[state.cur];
  }
  if (!nid || nid === 's-loading') {
    showScreen('s-loading', function () {
      runLoading();
    });
    return;
  }
  if (nid === 'mqi-a') buildMqiA();
  if (nid === 'mqi-b') buildMqiB();
  if (nid === 'ms1') setupMs1();
  if (nid === 'ms2') setupMs2();
  if (nid === 'react-a') setupReactA();
  if (nid === 'react-tried') setupReactTried();
  if (nid === 's4') setupS4();
  if (nid === 's20') setupS20();
  if (nid === 's26') setupS26();
  if (ECHO_SOURCE[nid]) injectEcho('echo-' + nid, echoFor(nid));
  showScreen(nid, function () {
    updateProgress();
    maybeToast();
  });
}

function goBack() {
  // No react-tried skip here (unlike next(), line 47): c8 can never be empty via the UI (s8's Continue is gated on a selection) — both functions must change together if that gate loosens.
  if (state.cur <= 0 || _navBusy) return;
  clearAutoAdvance();
  state.cur--;
  showScreen(FLOW[state.cur], function () {
    updateProgress();
    var nid = FLOW[state.cur];
    if (nid === 'ms1') setupMs1();
    if (nid === 'ms2') setupMs2();
    if (nid === 's4') setupS4();
    if (nid === 's20') setupS20();
    if (nid === 's26') setupS26();
    if (nid === 'react-tried') setupReactTried();
  });
}

function goTo(id, afterReveal) {
  showScreen(id, afterReveal);
}

function openFullPlanFromAnalysis() {
  goTo('s-result', function () {
    var topbar = document.getElementById('topbar');
    if (topbar) topbar.style.display = '';
    setPhaseLine(3, 1);
    var pp = document.getElementById('phasePill');
    if (pp) pp.textContent = 'Your plan';
    var bb = document.getElementById('backBtn');
    if (bb) bb.style.visibility = 'hidden';
    setupShareRow();
  });
}

function scrollFunnelToTop() {
  var reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
}

/** Platform-appropriate store link for the install-first result CTAs (Spec C §6 UTM tags). */


/** Gives a raw <svg viewBox="0 0 240 180" ...>...</svg> string an explicit size for embedding inside the 1080x1350 result-card template. Mirrors positionIllo() in scripts/build-share-images.mjs (Task 3), duplicated here in ~2 lines rather than shared across a Node/browser boundary. */
function sizedIllo(svgString, w, h) {
  return svgString.replace('<svg ', '<svg width="' + w + '" height="' + h + '" preserveAspectRatio="xMidYMid meet" ');
}

/** Share row (Spec A §4): "Share my loop" shares the loop-share page (with a rendered result card attached); "Send to the friend who overthinks" shares the quiz itself, never the result, with no card. */
function setupShareRow() {
  var row = document.getElementById('result-share-row');
  var mineBtn = document.getElementById('share-my-loop');
  var friendBtn = document.getElementById('share-to-friend');
  var supported =
    typeof navigator !== 'undefined' &&
    (typeof navigator.share === 'function' || (navigator.clipboard && typeof navigator.clipboard.writeText === 'function'));
  if (row) row.style.display = supported ? '' : 'none';
  if (!supported) return;

  var loopKey = state.D.c0 && LOOP_SHARE[state.D.c0] ? state.D.c0 : null;

  if (mineBtn) {
    mineBtn.onclick = function () {
      var l = loopKey ? LOOP_SHARE[loopKey] : null;
      var text = l
        ? 'Turns out I’m not anxious — I run ' + l.name + '. It has an off-switch.'
        : 'Turns out my mind runs a specific loop, not just “anxiety.” It has an off-switch.';
      var url = buildShareUrl(loopKey ? '/loops/' + loopKey + '.html' : '/', {});
      var finish = function (cardBlob) {
        shareResult({ text: text, url: url, cardBlob: cardBlob }).then(function (r) {
          if (r === 'copied') flashCopied(mineBtn);
        });
      };
      var source = loopKey && document.querySelector('.lp-tapin-card .illo[data-illo="loop-' + loopKey + '"] svg');
      if (l && source) {
        var cardSvg = SOCIAL_LOOP_SVG
          .split('{{ILLO}}').join(sizedIllo(source.outerHTML, 480, 360))
          .split('{{NAME}}').join(l.name)
          .split('{{URL}}').join('mindlifthere.com/loops/' + loopKey + '.html');
        renderCard(cardSvg).then(finish).catch(function () { finish(); });
      } else {
        finish();
      }
    };
  }
  if (friendBtn) {
    friendBtn.onclick = function () {
      var text = 'This 3-minute quiz finally named my 2am brain. I think you’re a Replayer:';
      var url = buildShareUrl('/', { src: 'friend' });
      shareResult({ text: text, url: url }).then(function (r) {
        if (r === 'copied') flashCopied(friendBtn);
      });
    };
  }
}

function showScreen(id, afterReveal) {
  var incoming = document.getElementById(id);
  if (!incoming) return;

  var outgoing = document.querySelector('.screen.active');
  var cb = typeof afterReveal === 'function' ? afterReveal : null;

  function runCb() {
    if (cb) cb();
  }

  function revealIn() {
    incoming.classList.add('active');
    scrollFunnelToTop();
    _navBusy = false;
    syncScreenSelectionsFromStore(incoming.id);
    runCb();
  }

  if (outgoing === incoming) {
    scrollFunnelToTop();
    syncScreenSelectionsFromStore(incoming.id);
    runCb();
    return;
  }

  if (outgoing) {
    _navBusy = true;
    outgoing.classList.add('is-out');
    var cleared = false;
    function finishOut() {
      if (cleared) return;
      cleared = true;
      clearTimeout(failSafe);
      outgoing.removeEventListener('animationend', onOut);
      outgoing.classList.remove('active', 'is-out');
      revealIn();
    }
    function onOut(ev) {
      if (ev.target !== outgoing) return;
      if (ev.animationName !== 'screenOut') return;
      finishOut();
    }
    var failSafe = setTimeout(finishOut, 520);
    outgoing.addEventListener('animationend', onOut);
  } else {
    revealIn();
  }
}

/** Matches header + milestone cards: progress through the flow (not including final loading step as 100%). */
function flowProgressPct(stepIndex) {
  var n = FLOW.length - 1;
  if (n <= 0) return 0;
  return Math.min(Math.round((stepIndex / n) * 100), 99);
}

/** Quiz questions (Q_IDS) remaining after the current step index, before loading. */
function remainingQuizStepsAfter(curIdx) {
  var count = 0;
  for (var i = curIdx + 1; i < FLOW.length; i++) {
    var id = FLOW[i];
    if (id === 's-loading') break;
    if (Q_IDS.indexOf(id) >= 0) count++;
  }
  return count;
}

/** Headway-style phase line: mark nodes done/current and fill the current segment by position within that node's screens. */
function setPhaseLine(node, within) {
  var nodes = document.querySelectorAll('#progPhases .pp-node');
  for (var i = 0; i < nodes.length; i++) {
    nodes[i].classList.toggle('is-done', i < node);
    nodes[i].classList.toggle('is-current', i === node);
  }
  for (var s = 0; s < 3; s++) {
    var fill = document.getElementById('ppFill' + s);
    if (!fill) continue;
    var w = node === 3 ? 100 : s < node ? 100 : s === node ? Math.round(within * 100) : 0;
    fill.style.width = w + '%';
  }
}

function updateProgress() {
  var sid = FLOW[state.cur];
  var topbar = document.getElementById('topbar');
  if (topbar) topbar.style.display = '';
  var node = phaseNodeFor(sid);
  var ids = FLOW.filter(function (id) { return phaseNodeFor(id) === node; });
  var pos = ids.indexOf(sid);
  var within = ids.length > 1 ? pos / (ids.length - 1) : 1;
  if (sid === 's-land' || sid === 's-welcome') within = 0;
  setPhaseLine(node, within);
  var pill = document.getElementById('phasePill');
  if (pill) pill.textContent = PHASES[sid] || '';
  var bb = document.getElementById('backBtn');
  if (bb) bb.style.visibility = state.cur > 0 ? 'visible' : 'hidden';
}

function maybeToast() {
  const t = TOASTS.find(function (x) { return x.at === FLOW[state.cur]; });
  if (!t) return;
  const el = document.getElementById('mToast');
  el.textContent = t.msg;
  el.classList.add('show');
  setTimeout(function () { el.classList.remove('show'); }, 2800);
}

/** Multi-select question groups: choice container id → Continue button id */
var MULTI_CHOICE_BTNS = { c5: 'btn-s5', c8: 'btn-s8', c13: 'btn-s13', c19: 'btn-s19', csq2: 'btn-sq2' };

/** Screen ids that use multi-select (for restoring UI from D when navigating back) */
var MULTI_CHOICE_SCREENS = { s5: 'c5', s8: 'c8', s13: 'c13', s19: 'c19', sq2: 'csq2' };

function updateMultiChoiceButton(gid) {
  var bid = MULTI_CHOICE_BTNS[gid];
  var root = document.getElementById(gid);
  if (!bid || !root) return;
  var btn = document.getElementById(bid);
  if (btn) btn.disabled = root.querySelectorAll('.choice.sel').length === 0;
}

/**
 * Re-apply .sel classes from D[gid] when entering a multi-select screen (back/forward).
 */
function syncMultiChoiceUIFromStore(screenId) {
  var gid = MULTI_CHOICE_SCREENS[screenId];
  if (!gid) return;
  var root = document.getElementById(gid);
  if (!root) return;
  var stored = state.D[gid];
  root.querySelectorAll('.choice').forEach(function (c) {
    c.classList.remove('sel');
  });
  if (Array.isArray(stored) && stored.length) {
    root.querySelectorAll('.choice').forEach(function (c) {
      if (stored.indexOf(c.dataset.v) >= 0) c.classList.add('sel');
    });
  } else if (stored != null && stored !== '' && typeof stored === 'string') {
    root.querySelectorAll('.choice').forEach(function (c) {
      if (c.dataset.v === stored) c.classList.add('sel');
    });
  }
  updateMultiChoiceButton(gid);
}

/**
 * Single-select screens: [ 'choice'|'scale'|'grid', containerId ]
 * Used to restore selection + Continue state when going back.
 */
var SINGLE_INPUT_BY_SCREEN = {
  s0: ['choice', 'c0'],
  s1: ['scale', 'c1'],
  s2: ['choice', 'c2'],
  s3: ['grid', 'c3'],
  s6: ['scale', 'c6'],
  s7: ['choice', 'c7'],
  s10: ['choice', 'c10'],
  s11: ['scale', 'c11'],
  s12: ['scale', 'c12'],
  sq1: ['choice', 'csq1'],
  s15: ['choice', 'c15'],
  s16: ['scale', 'c16'],
  s17: ['scale', 'c17'],
  s18: ['choice', 'c18'],
  s21: ['grid', 'c21'],
  s22: ['grid', 'c22'],
  s23: ['choice', 'c23'],
  s24: ['choice', 'c24'],
  s25: ['choice', 'c25'],
  s27: ['scale', 'c27'],
  s28: ['choice', 'c28'],
  s29: ['scale', 'c29'],
  s30: ['choice', 'c30'],
  s31: ['scale', 'c31'],
  s32: ['scale', 'c32']
};

function syncSingleInputFromStore(screenId) {
  var spec = SINGLE_INPUT_BY_SCREEN[screenId];
  if (!spec) return;
  var kind = spec[0];
  var gid = spec[1];
  var stored = state.D[gid];
  if (Array.isArray(stored)) return;

  var has = stored != null && stored !== '';
  var str = has ? String(stored) : '';

  if (kind === 'choice') {
    var cr = document.getElementById(gid);
    if (!cr) return;
    cr.querySelectorAll('.choice').forEach(function (c) {
      c.classList.toggle('sel', has && c.dataset.v === str);
    });
  } else if (kind === 'scale') {
    var sr = document.getElementById(gid);
    if (!sr) return;
    sr.querySelectorAll('.scale-btn').forEach(function (c) {
      c.classList.toggle('sel', has && c.dataset.v === str);
    });
    var vm = document.getElementById('vm-' + screenId);
    if (vm) vm.classList.toggle('show', has);
  } else if (kind === 'grid') {
    var gr = document.getElementById(gid);
    if (!gr) return;
    gr.querySelectorAll('.gchoice').forEach(function (c) {
      c.classList.toggle('sel', has && c.dataset.v === str);
    });
  }

  var btn = document.getElementById('btn-' + screenId);
  if (btn) btn.disabled = !has;
}

function syncScreenSelectionsFromStore(screenId) {
  syncMultiChoiceUIFromStore(screenId);
  syncSingleInputFromStore(screenId);
  if (screenId === 's34') {
    var ni = document.getElementById('nameInput');
    if (ni && typeof state.D.name === 'string' && state.D.name.length) {
      ni.value = state.D.name;
      checkName();
    }
  }
}

function pick(el, gid, mode, sid) {
  if (mode === 's') {
    var rootS = document.getElementById(gid);
    if (!rootS || _navBusy) return;
    var v = el.dataset.v;
    if (v === undefined || v === '') return;
    // Re-tap same answer: keep state, do not queue another auto-advance
    if (el.classList.contains('sel') && rootS.querySelectorAll('.choice.sel').length === 1 && state.D[gid] === v) {
      return;
    }
    clearAutoAdvance();
    rootS.querySelectorAll('.choice').forEach(function (c) {
      c.classList.remove('sel');
    });
    el.classList.add('sel');
    state.D[gid] = v;
    var btn = document.getElementById('btn-' + sid);
    if (btn) btn.disabled = false;
    _autoAdvanceTimer = setTimeout(function () {
      _autoAdvanceTimer = null;
      if (!_navBusy) next();
    }, 460);
    return;
  }

  // Multi-select: toggle, store ordered list of data-v, enable Continue when ≥1 selected
  var v = el.dataset.v;
  var exclusiveNone = gid === 'c8' || gid === 'c13';
  var exclusiveFirst = gid === 'c19';

  if (exclusiveNone && v === 'none') {
    el.classList.toggle('sel');
    if (el.classList.contains('sel')) {
      document.querySelectorAll('#' + gid + ' .choice').forEach(function (c) {
        if (c !== el) c.classList.remove('sel');
      });
    }
  } else if (exclusiveFirst && v === 'first') {
    el.classList.toggle('sel');
    if (el.classList.contains('sel')) {
      document.querySelectorAll('#' + gid + ' .choice').forEach(function (c) {
        if (c !== el) c.classList.remove('sel');
      });
    }
  } else {
    el.classList.toggle('sel');
    if (el.classList.contains('sel')) {
      if (exclusiveNone) {
        var noneEl = document.querySelector('#' + gid + ' .choice[data-v="none"]');
        if (noneEl) noneEl.classList.remove('sel');
      }
      if (exclusiveFirst) {
        var firstEl = document.querySelector('#' + gid + ' .choice[data-v="first"]');
        if (firstEl) firstEl.classList.remove('sel');
      }
    }
  }

  state.D[gid] = [].map.call(document.querySelectorAll('#' + gid + ' .choice.sel'), function (c) {
    return c.dataset.v;
  });
  updateMultiChoiceButton(gid);
}

function pickScale(el, gid, sid) {
  var root = document.getElementById(gid);
  if (!root || _navBusy) return;
  var v = el.dataset.v;
  if (v === undefined || v === '') return;
  if (el.classList.contains('sel') && root.querySelectorAll('.scale-btn.sel').length === 1 && state.D[gid] === v) {
    return;
  }
  clearAutoAdvance();
  root.querySelectorAll('.scale-btn').forEach(function (c) {
    c.classList.remove('sel');
  });
  el.classList.add('sel');
  state.D[gid] = v;
  var vm = document.getElementById('vm-' + sid);
  if (vm) vm.classList.add('show');
  var btn = document.getElementById('btn-' + sid);
  if (btn) btn.disabled = false;
  _autoAdvanceTimer = setTimeout(function () {
    _autoAdvanceTimer = null;
    if (!_navBusy) next();
  }, 560);
}

function pickG(el, gid, sid) {
  var root = document.getElementById(gid);
  if (!root || _navBusy) return;
  var v = el.dataset.v;
  if (v === undefined || v === '') return;
  if (el.classList.contains('sel') && root.querySelectorAll('.gchoice.sel').length === 1 && state.D[gid] === v) {
    return;
  }
  clearAutoAdvance();
  root.querySelectorAll('.gchoice').forEach(function (c) {
    c.classList.remove('sel');
  });
  el.classList.add('sel');
  state.D[gid] = v;
  var btn = document.getElementById('btn-' + sid);
  if (btn) btn.disabled = false;
  _autoAdvanceTimer = setTimeout(function () {
    _autoAdvanceTimer = null;
    if (!_navBusy) next();
  }, 460);
}

function checkName() {
  const v = document.getElementById('nameInput').value.trim();
  state.D.name = v;
  const btn = document.getElementById('btn-s34');
  btn.disabled = v.length < 2;
  if (v.length >= 2) {
    document.getElementById('vm-name').classList.add('show');
    btn.textContent = "Build " + v.split(' ')[0] + "'s profile";
  } else {
    document.getElementById('vm-name').classList.remove('show');
    btn.textContent = 'Build my profile';
  }
}

function unlockResults() {
  buildAnalysis();
  goTo('s-analysis');
  var topbar = document.getElementById('topbar');
  if (topbar) topbar.style.display = '';
  setPhaseLine(3, 1);
  document.getElementById('phasePill').textContent = '';
  document.getElementById('backBtn').style.visibility = 'hidden';
}

function bucketFor(n) {
  return n <= 2 ? 'low' : n === 3 ? 'mid' : 'high';
}

/** Personalise spec §10: looks up the acknowledgment line for the screen `sid` is about to show, from ECHOES/ECHO_SOURCE. */
function echoFor(sid) {
  const src = ECHO_SOURCE[sid];
  if (!src) return '';
  const [gid, kind] = src;
  const table = ECHOES[gid];
  if (!table) return '';
  const val = state.D[gid];

  if (kind === 'multi') {
    if (!Array.isArray(val) || !val.length) return '';
    const first = table[val[0]];
    if (!first) return '';
    const line = val.length > 1 ? first + (table.more || '') : first;
    return '<p class="echo-main">' + line + '</p>';
  }
  if (kind === 'scale') {
    const n = parseInt(val, 10);
    if (!n) return '';
    const line = table[bucketFor(n)];
    return line ? '<p class="echo-main">' + line + '</p>' : '';
  }
  // 'choice' (includes grid-style single-select containers)
  const line = val && table[val];
  return line ? '<p class="echo-main">' + line + '</p>' : '';
}

function injectEcho(id, html) {
  if (!html) return;
  const el = document.getElementById(id);
  if (el) {
    el.innerHTML = html;
    // The margin-note rail takes the person's loop tint (falls back to accent via CSS).
    const tint = state.D.c0 && LOOP_TINT[state.D.c0];
    if (tint) el.style.setProperty('--echo-tint', 'var(' + tint + ')');
    el.classList.add('show');
  }
}

function buildMqiA() {
  const loop = state.D.c0 || 'rumination';
  const statMap = { rumination: '79%', anticipation: '82%', critic: '76%', scatter: '84%', shutdown: '71%', night: '88%' };
  if (typeof isBalancedMindProfile === 'function' && isBalancedMindProfile()) {
    document.getElementById('mqi-a-text').innerHTML = BALANCED_MQI_A_BODY;
    document.getElementById('mqi-a-fact').innerHTML = BALANCED_MQI_A_FACT;
  } else {
    const row = MQI_A[loop] || MQI_A.rumination;
    document.getElementById('mqi-a-text').innerHTML = row.body;
    document.getElementById('mqi-a-fact').innerHTML = MQI_A_FACT;
  }
  document.getElementById('mqi-a-n').textContent = statMap[loop] || '78%';
  document.getElementById('mqi-a-l').textContent =
    'with a similar early loop profile felt a clear shift in week one with MindLift';
}

function buildMqiB() {
  const loop = state.D.c0 || 'rumination';
  const pctMap = { rumination: '82%', anticipation: '79%', critic: '84%', scatter: '88%', shutdown: '74%', night: '91%' };
  if (typeof isBalancedMindProfile === 'function' && isBalancedMindProfile()) {
    document.getElementById('mqi-b-text').innerHTML = stripLeadingLoopProfileTitle(BALANCED_MAIN_PATTERN);
    document.getElementById('mqi-b-implication').innerHTML = BALANCED_MQI_B_TRAINING;
  } else {
    const row = MQI_B[loop] || MQI_B.rumination;
    document.getElementById('mqi-b-text').innerHTML = stripLeadingLoopProfileTitle(row.pattern);
    document.getElementById('mqi-b-implication').innerHTML = row.training;
  }
  document.getElementById('mqi-b-n').textContent = pctMap[loop] || '84%';
  document.getElementById('mqi-b-l').textContent =
    'with a similar loop profile made meaningful progress within ~10 days';
}

/** Landing tap-in: the first tap answers Q1 (c0), then navigates to s-welcome. */
function landPick(v) {
  if (_navBusy) return;
  state.D.c0 = v;
  var idx = FLOW.indexOf('s-welcome');
  if (idx < 0) return;
  state.cur = idx;
  showScreen('s-welcome', function () {
    updateProgress();
  });
}

/** Continue from s-land2: skip s0 if the landing tap already answered it. */
function landContinue() {
  if (_navBusy) return;
  var target = state.D.c0 ? 's1' : 's0';
  var idx = FLOW.indexOf(target);
  if (idx < 0) return;
  state.cur = idx;
  if (target === 's1') injectEcho('echo-s1', echoFor('s1'));
  showScreen(target, function () {
    updateProgress();
    maybeToast();
  });
}

/** Reaction screen after readiness (c32): acknowledge the answer, no fake urgency. */
function setupReactA() {
  var row = REACT_READY[state.D.c32] || REACT_READY[3];
  var t = document.getElementById('react-a-title');
  var b = document.getElementById('react-a-body');
  if (t) t.innerHTML = row.title;
  if (b) b.innerHTML = row.body;
}

/** react-tried — reacts to c8 ("what have you tried"); skipped entirely when c8 is empty (see next()). */
function setupReactTried() {
  const picks = Array.isArray(state.D.c8) ? state.D.c8 : [];
  const hed = document.getElementById('rt-hed');
  if (hed) hed.textContent = triedHeadline(picks);
  const body = document.getElementById('rt-body');
  if (body) {
    // Cap at two reactions: enough to feel seen, not a wall of text.
    body.innerHTML = picks
      .slice(0, 2)
      .map(function (v) {
        return TRIED_REACTIONS[v] ? '<p>' + TRIED_REACTIONS[v] + '</p>' : '';
      })
      .join('') + (picks.length > 2 ? '<p class="rt-more">The rest follow the same pattern \u2014 they calm the thought, they don\u2019t interrupt the loop.</p>' : '');
  }
  document.querySelectorAll('.tried-tile').forEach(function (tile) {
    tile.classList.toggle('is-picked', picks.indexOf(tile.getAttribute('data-v')) >= 0);
  });
  const illoSlot = document.getElementById('rt-loop-illo');
  if (illoSlot) {
    const source = state.D.c0 && document.querySelector('.lp-tapin-card .illo[data-illo="loop-' + state.D.c0 + '"] svg');
    illoSlot.innerHTML = source ? source.outerHTML : '';
  }
}

function setupMs1() {
  const br = '<br><br>';
  const loopMap = {
    rumination: `Next, we’ll look at what keeps your mind returning to certain moments.${br}We’ll focus on helping your mind let moments land.`,
    anticipation: `Next, we’ll look at what pulls your mind into future “what ifs”.${br}We’ll focus on more space between thought and reaction.`,
    critic: `Next, we’ll look at what triggers that critical voice.${br}We’ll explore growth without the constant pressure.`,
    scatter: `Next, we’ll look at what pulls your attention in different directions.${br}We’ll help you find something steady to come back to.`,
    shutdown: `Next, we’ll look at what leads you to shut down or feel stuck.${br}We’ll move gently toward getting things unstuck.`,
    night: `Next, we’ll look at what keeps your mind active at night.${br}We’ll focus on helping it wind down instead.`
  };
  const el = document.getElementById('ms1-insight');
  if (el) {
    if (typeof isBalancedMindProfile === 'function' && isBalancedMindProfile()) {
      el.innerHTML = BALANCED_MS1;
    } else {
      el.innerHTML = loopMap[state.D.c0 || 'rumination'];
    }
  }
  const patternEl = document.getElementById('ms1-pattern');
  if (patternEl) {
    const sentence = patternSentence(state.D);
    patternEl.textContent = sentence;
    patternEl.hidden = !sentence;
  }
  const bodyPicks = Array.isArray(state.D.c13) ? state.D.c13.filter(function (v) { return v && v !== 'none'; }) : [];
  const bm = document.getElementById('ms1-bodymap');
  if (bm) {
    // .hidden is a no-op on SVG elements in this engine (SVGElement.prototype has no 'hidden'
    // IDL accessor, unlike HTMLElement) — toggle the content attribute directly so [hidden] CSS matches.
    if (bodyPicks.length) bm.removeAttribute('hidden');
    else bm.setAttribute('hidden', '');
    if (bodyPicks.length) {
      bm.querySelectorAll('[class^="bm-"]').forEach(function (z) { z.classList.remove('is-tinted'); });
      const zoneSel = { chest: '.bm-chest', stomach: '.bm-stomach', head: '.bm-head', jaw: '.bm-jaw', fatigue: '.bm-fatigue' };
      bodyPicks.forEach(function (p) {
        const sel = zoneSel[p];
        const z = sel && bm.querySelector(sel);
        if (z) z.classList.add('is-tinted');
      });
    }
  }
  const clockEl = document.getElementById('ms1-clock');
  if (clockEl) {
    const hasTime = !!state.D.c2;
    // Same SVG-namespace .hidden caveat as the body-map above.
    if (hasTime) clockEl.removeAttribute('hidden');
    else clockEl.setAttribute('hidden', '');
    if (hasTime) {
      clockEl.querySelectorAll('[class^="ck-"]').forEach(function (z) { z.classList.remove('is-shaded'); });
      const band = clockEl.querySelector('.ck-' + state.D.c2);
      if (band) band.classList.add('is-shaded');
    }
  }
  var p1 = flowProgressPct(state.cur);
  setTimeout(function () {
    document.getElementById('ms1-bar').style.width = p1 + '%';
  }, 200);
}

/** s4 — method screen reads the loop back (Personalise spec §3). */
function setupS4() {
  const loop = state.D.c0;
  const m = loop && LOOP_METHOD[loop];
  const hed = document.getElementById('s4-hed');
  if (hed) {
    hed.innerHTML = m
      ? 'Not calm. Not manage.<br><em>Respond differently to ' + m.short + '.</em>'
      : 'Not calm. Not manage.<br><em>Respond differently.</em>';
  }
  const node0 = document.getElementById('s4-node0');
  if (node0) node0.textContent = m ? m.thought : 'Thought appears';
  const illoSlot = document.getElementById('s4-loop-illo');
  if (illoSlot) {
    const source = loop && document.querySelector('.lp-tapin-card .illo[data-illo="loop-' + loop + '"] svg');
    illoSlot.innerHTML = source ? source.outerHTML : '';
  }
}

/** s20 — community screen names the loop (Personalise spec §3). */
function setupS20() {
  const loop = state.D.c0;
  const m = loop && LOOP_METHOD[loop];
  const lead = document.getElementById('s20-lead');
  if (lead) {
    lead.innerHTML = m
      ? '<strong>64k people</strong> have learned to respond to ' + m.short + ' differently — not fight it, not suppress it.'
      : '<strong>64k people</strong> have learned to respond to their thoughts differently — not fight them, not suppress them.';
  }
  const chip = document.getElementById('s20-chip');
  if (chip) {
    const emblemMap = { rumination: '🔁', anticipation: '🌀', critic: '📢', scatter: '💨', shutdown: '🪨', night: '🌙' };
    if (loop && LOOP_SHARE[loop]) {
      chip.textContent = (emblemMap[loop] || '') + ' Same loop as you: ' + LOOP_SHARE[loop].name;
      chip.hidden = false;
    } else {
      chip.hidden = true;
    }
  }
}

/** s26 — testimonial pair by loop, with an "Illustrative examples" note when either card is marked illustrative (Personalise spec §3, testimonials task). */
function setupS26() {
  const loop = state.D.c0;
  const pair = (loop && LOOP_TESTIMONIALS[loop]) || LOOP_TESTIMONIALS.rumination;
  const tintVar = loop && LOOP_TINT[loop];
  [1, 2].forEach(function (n) {
    const t = pair[n - 1];
    const qEl = document.getElementById('s26-t' + n + '-q');
    const avEl = document.getElementById('s26-t' + n + '-av');
    const nameEl = document.getElementById('s26-t' + n + '-name');
    const roleEl = document.getElementById('s26-t' + n + '-role');
    if (qEl) qEl.textContent = t.quote;
    if (avEl) {
      avEl.textContent = t.avatar;
      avEl.style.background = tintVar ? 'var(' + tintVar + ')' : '';
    }
    if (nameEl) nameEl.textContent = t.name;
    if (roleEl) roleEl.textContent = t.role;
  });
  const note = document.getElementById('s26-illustrative-note');
  if (note) note.hidden = !pair.some(function (t) { return t.illustrative; });
}

function setupMs2() {
  const name = (state.D.name || '').split(' ')[0];
  const remaining = remainingQuizStepsAfter(state.cur);
  const qPhrase = remaining === 1 ? '1 question' : remaining + ' questions';
  const loop = state.D.c0;
  const short = loop && LOOP_METHOD[loop] && LOOP_METHOD[loop].short;
  const msgEl = document.getElementById('ms2-msg');
  if (msgEl) {
    if (short) {
      msgEl.textContent =
        (name ? name + ', ' : '') +
        (name ? "we're" : "We're") +
        ' sharpening your plan for ' +
        short +
        ' — just ' +
        qPhrase +
        ' left before the full reveal.';
    } else {
      msgEl.textContent = name
        ? `${name}, we're sharpening your loop profile — just ${qPhrase} left before the full reveal.`
        : `We're sharpening your loop profile — just ${qPhrase} left before the full reveal.`;
    }
  }
  var btn = document.getElementById('btn-ms2');
  if (btn) {
    btn.textContent = remaining === 1 ? 'Continue — 1 question left' : 'Continue — ' + remaining + ' questions left';
  }
  const flag = document.getElementById('ms2-flag');
  if (flag) flag.style.fill = loop && LOOP_TINT[loop] ? 'var(' + LOOP_TINT[loop] + ')' : '';
  var p2 = flowProgressPct(state.cur);
  setTimeout(function () {
    document.getElementById('ms2-bar').style.width = p2 + '%';
  }, 200);
}

function runLoading() {
  var topbarEl = document.getElementById('topbar');
  if (topbarEl) topbarEl.style.display = 'none';
  const name = (state.D.name || '').split(' ')[0];
  document.getElementById('loading-for').textContent = name
    ? `Assembling ${name}'s loop profile...`
    : 'Assembling your loop profile...';
  var subEl = document.getElementById('loading-sub');
  if (subEl) {
    subEl.textContent = name
      ? `Stitching together ${name}'s profile from ${Q_IDS.length} answers`
      : 'Stitching together your loop profile from ' + Q_IDS.length + ' answers';
  }
  var pctEl = document.getElementById('loading-pct');
  var pctShown = 0;
  var pctTimer = setInterval(function () {
    // Ease toward the current step's share of 100% so the counter never outruns the steps
    var target = Math.min(99, Math.round((i / 6) * 100));
    if (pctShown < target) {
      pctShown++;
      if (pctEl) pctEl.textContent = pctShown + '%';
    }
  }, 60);
  let i = 0;
  const tick = function () {
    if (i > 0) {
      const p = document.getElementById('ls' + (i - 1));
      p.classList.remove('act');
      p.classList.add('done');
      p.querySelector('.ls-dot').textContent = '✓';
    }
    if (i < 6) {
      document.getElementById('ls' + i).classList.add('act');
      i++;
      setTimeout(tick, 900 + Math.random() * 400);
    } else {
      clearInterval(pctTimer);
      if (pctEl) pctEl.textContent = '100%';
      setTimeout(function () {
        unlockResults();
      }, 500);
    }
  };
  tick();
}

export {
  next,
  goBack,
  pick,
  pickScale,
  pickG,
  checkName,
  landPick,
  landContinue,
  openFullPlanFromAnalysis
};

updateProgress();
